"use client";

import { Calendar, Tag, User } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="h-10 w-3/4 rounded-lg bg-muted animate-pulse mb-6" />
        <div className="h-72 w-full rounded-xl bg-muted animate-pulse mb-8" />
        <div className="space-y-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-4 w-full rounded bg-muted animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center text-muted-foreground">
        <p>Failed to load project details.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center text-muted-foreground">
        <p>No project found.</p>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "BT Remodeling";
  const authorAvatar = post.author?.avatar?.["96"] || post.author?.avatar?.["48"] || "";
  const authorInitials = authorName
    .split(" ")
    .map((n: string) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  return (
    <article className="max-w-3xl mx-auto px-4 sm:px-6">
      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {post.categories.map((cat, i) => (
            <span
              data-index={i}
              key={cat.id}
              className="inline-flex items-center gap-1 text-xs font-default font-medium px-3 py-1 rounded-md"
              style={{ background: "var(--secondary)", color: "var(--secondary-foreground)" }}
            >
              <Tag className="size-3" />
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground leading-tight mb-6 tracking-tight">
        {post.title}
      </h1>

      {}
      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
        <span className="flex items-center gap-1.5">
          <User className="size-4" style={{ color: "var(--secondary)" }} />
          {authorName}
        </span>
        {formattedDate && (
          <span className="flex items-center gap-1.5">
            <Calendar className="size-4" style={{ color: "var(--secondary)" }} />
            {formattedDate}
          </span>
        )}
      </div>

      {}
      {post.featuredImage && (
        <div className="rounded-md overflow-hidden mb-10 shadow-xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full object-cover max-h-[500px]"
          />
        </div>
      )}

      {}
      {post.content && (
        <div
          className="prose prose-invert max-w-none text-foreground font-default leading-relaxed"
          style={{
            fontSize: "1.125rem",
            lineHeight: "1.8",
            color: "var(--foreground)",
          }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-10">
          {post.tags.map((tag, i) => (
            <span
              data-index={i}
              key={tag.id}
              className="text-xs px-3 py-1 rounded-md border text-muted-foreground font-default"
              style={{ borderColor: "var(--border)" }}
            >
              #{tag.name}
            </span>
          ))}
        </div>
      )}

      <Separator className="my-12" />

      {}
      <div
        className="rounded-md p-6 sm:p-8 flex flex-col sm:flex-row gap-5 items-start sm:items-center mb-16 shadow-md"
        style={{ background: "var(--card)", border: "1px solid var(--border)" }}
      >
        <Avatar className="size-16 shrink-0 rounded-md">
          <AvatarImage src={authorAvatar} alt={authorName} />
          <AvatarFallback
            className="text-lg font-default font-semibold rounded-md"
            style={{ background: "var(--primary)", color: "var(--primary-foreground)" }}
          >
            {authorInitials}
          </AvatarFallback>
        </Avatar>
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 font-default">Project Lead</p>
          <h3 className="font-default text-lg font-semibold text-foreground mb-1">{authorName}</h3>
          <p className="text-sm text-muted-foreground font-default">
            Licensed residential general contractor specializing in high-end craftsmanship and modern home transformations in Denver.
          </p>
        </div>
      </div>
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-52 rounded-md bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {posts.map((post, i) => (
        <div data-index={i} key={post.id}>
          <Card
            className="h-full rounded-md overflow-hidden border shadow-sm"
            style={{
              background: "var(--card)",
              borderColor: "var(--border)",
            }}
          >
            {post.featuredImage && (
              <div className="overflow-hidden h-40">
                <img
                  src={post.featuredImage}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            )}
            <CardHeader className="pb-2">
              <CardTitle className="font-default text-base font-semibold text-foreground leading-snug line-clamp-2">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {post.excerpt && (
                <p
                  className="text-sm text-muted-foreground line-clamp-2 mb-4 font-default"
                  dangerouslySetInnerHTML={{ __html: post.excerpt }}
                />
              )}
              <Link to={post.link || "#"}>
                <Button variant="outline" size="sm" className="w-full font-default rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                  View Project
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      ))}
    </div>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedWPQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc",
  });

  return (
    <section data-section-id="230879"
      id="single-post-template-section"
      className="bg-background text-foreground py-16"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostConsumer />
      </PostsProvider>

      {}
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <Separator className="mb-12" />
        <div className="mb-8">
          <h2 className="font-default text-2xl font-bold text-foreground mb-2">Recent Transformations</h2>
          <p className="text-sm text-muted-foreground font-default">
            Explore more high-end residential remodeling projects by BT Remodeling.
          </p>
        </div>
        <PostsProvider wp_query={{ post_type: "post", per_page: 3, orderby: "date", order: "desc" }}>
          <RelatedPostsConsumer />
        </PostsProvider>
        <div className="mt-10 flex justify-center">
          <Link to="/OurPortfolioPage">
            <Button variant="outline" size="lg" className="font-default rounded-md px-8">
              View Full Portfolio
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}